/**
 * Direct JSON-RPC helpers for the SRC-20 read path.
 *
 * Ported from `packaged/safe-app-src20/src/seismic/rpc.ts`. We talk to the
 * Seismic RPC directly (not through seismic-viem) because the read flow uses
 * a hand-built type-0x4A envelope.
 */
import type { Hex } from 'viem'

type JsonRpcResponse<T> = { result?: T; error?: { code?: number; message?: string; data?: unknown } }

export async function rpc<T>(rpcUrl: string, method: string, params: unknown[] = []): Promise<T> {
  const res = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  })
  if (!res.ok) throw new Error(`RPC ${method} HTTP ${res.status}`)
  const json = (await res.json()) as JsonRpcResponse<T>
  if (json.error) {
    const message = json.error.message ?? 'rpc error'
    const data = json.error.data
    const dataStr = typeof data === 'string' ? data : data != null ? JSON.stringify(data) : ''
    const dataSuffix = dataStr ? ` (data=${dataStr.length > 26 ? dataStr.slice(0, 26) + '…' : dataStr})` : ''
    throw new Error(`RPC ${method}: ${message}${dataSuffix}`)
  }
  if (json.result === undefined) throw new Error(`RPC ${method}: missing result`)
  return json.result
}

/**
 * Fetch the 33-byte TEE pubkey via `seismic_getTeePublicKey`. Cached per
 * rpcUrl because it doesn't change between epochs.
 */
const teePubkeyCache = new Map<string, Uint8Array>()
export async function fetchTeePubkey(rpcUrl: string): Promise<Uint8Array> {
  const hit = teePubkeyCache.get(rpcUrl)
  if (hit) return hit
  const result = await rpc<string>(rpcUrl, 'seismic_getTeePublicKey', [])
  const hex = result.startsWith('0x') ? result.slice(2) : result
  if (hex.length !== 66) throw new Error(`TEE pubkey expected 33 bytes, got ${hex.length / 2}`)
  const bytes = new Uint8Array(33)
  for (let i = 0; i < 33; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  teePubkeyCache.set(rpcUrl, bytes)
  return bytes
}

export async function fetchLatestBlock(rpcUrl: string): Promise<{ hash: Hex; number: bigint }> {
  const blk = await rpc<{ hash: Hex; number: Hex }>(rpcUrl, 'eth_getBlockByNumber', ['latest', false])
  return { hash: blk.hash, number: BigInt(blk.number) }
}

export async function getTransactionCount(rpcUrl: string, address: Hex): Promise<bigint> {
  const r = await rpc<Hex>(rpcUrl, 'eth_getTransactionCount', [address, 'pending'])
  return BigInt(r)
}

/**
 * Seismic's `Bytes` variant of `eth_call`: pass the raw type-0x4A envelope
 * (with signed_read=true). The RPC recovers `from` from the envelope
 * signature, executes `msg.sender = recovered EOA`, and returns the
 * AES-256-GCM-encrypted plaintext.
 */
export async function ethCallRaw(rpcUrl: string, rawHex: Hex): Promise<Hex> {
  return rpc<Hex>(rpcUrl, 'eth_call', [rawHex, 'latest'])
}
